// CASH1-S4 (docs/build-briefs/CASH1-S4-closeout-cash-and-tie-outs.md; PRD P1.1; DEC-399; design mockup cash1-7-closeout-cash): draws
// the closeout Cash block from cashView(). All text via textContent. Reuses the closeout's own figure, list and row styles.

import type { CloseoutSummaryDto } from "./api.ts";
import { cashView, type CashSessionRow } from "./closeoutCashModel.ts";

function el(tag: string, className: string, testid?: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className !== "") node.className = className;
  if (testid !== undefined) node.dataset["testid"] = testid;
  if (text !== undefined) node.textContent = text;
  return node;
}

const clock = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/** CLOSEFIX1: a variance line reads "Miscounted change · Approved by Owen Castellano at 11:42 PM", so the reason, the
 * approver and the time are on the closeout before the day is finalized. The close event is the approval. */
function approvalText(row: CashSessionRow): string {
  if (!row.approved) return "";
  const who = row.approverName === null ? "Approved" : `Approved by ${row.approverName}`;
  const reason = row.reason === null ? "" : ` · ${row.reason}`;
  return `${reason} · ${who} at ${clock(row.closedAt)}`;
}

function sessionRow(row: CashSessionRow): HTMLElement {
  const wrap = el("div", "activity-row", "closeout-cash-session");
  const main = el("div", "");
  main.append(
    el("div", "activity-name", undefined, row.title),
    el(
      "div",
      "activity-detail",
      undefined,
      `${clock(row.openedAt)} to ${clock(row.closedAt)} · Counted ${row.countedText}${approvalText(row)}`,
    ),
  );
  wrap.append(main, el("span", "activity-tag", "closeout-cash-session-tag", row.tag));
  return wrap;
}

/** Fills the Cash host from the summary, or hides it. */
export function renderCloseoutCash(host: HTMLElement, summary: CloseoutSummaryDto): void {
  host.innerHTML = "";
  const view = cashView({
    cashEnabled: summary.cashEnabled,
    cash: summary.cash,
    finalized: summary.finalization !== null,
  });
  host.hidden = view.kind === "hidden";
  if (view.kind === "hidden") return;
  host.append(el("h2", "section-title", undefined, "Cash"));
  host.append(
    el(
      "p",
      "specials-section-note",
      "closeout-cash-note",
      "What each drawer counted against what it should hold. Review any short or over drawer and its reason before you finalize.",
    ),
  );
  if (view.kind === "na") {
    host.append(el("p", "list-card-empty", "closeout-cash-na", "No cash figures were recorded for this day."));
    return;
  }
  const grid = el("div", "figure-grid", "closeout-cash-tiles");
  for (const tile of view.tiles) {
    const card = el("div", "figure-card", "closeout-cash-tile");
    const value = el(
      "div",
      tile.tone === "over" || tile.tone === "short"
        ? "figure-card-value info"
        : "figure-card-value",
      undefined,
      tile.value,
    );
    card.append(el("div", "figure-card-label", undefined, tile.label), value);
    grid.append(card);
  }
  host.append(grid);

  const lines = el("div", "list-card", "closeout-cash-lines");
  for (const line of view.lines) {
    const row = el("div", "activity-row", "closeout-cash-line");
    const label = el("div", "activity-name", undefined, line.label);
    const value = el(
      "span",
      line.strong === true ? "activity-name" : "activity-detail",
      undefined,
      line.value,
    );
    row.append(label, value);
    lines.append(row);
  }
  host.append(lines);

  for (const session of view.sessions) host.append(sessionRow(session));
  if (view.openDrawers.length > 0) {
    const open = el("div", "list-card", "closeout-cash-open");
    open.append(el("div", "activity-row", undefined, "Drawers open"));
    for (const drawer of view.openDrawers) {
      const row = el("div", "activity-row", "closeout-cash-open-row");
      row.append(
        el("div", "activity-detail", undefined, `${drawer.title}, since ${clock(drawer.openedAt)}`),
      );
      open.append(row);
    }
    host.append(open);
  }
}
